/**
 * StorageLocationCard Component
 *
 * Manages connection to local directory for sync storage.
 * Shows connection status, directory path, and connect/disconnect actions.
 */

// 1. External dependencies
import React, { useState, useEffect } from 'react';
import { HardDrive, FolderOpen, Link as LinkIcon, Unlink, Info } from 'lucide-react';

// 2. Internal absolute imports
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Chip,
  Code,
  Popover,
  PopoverTrigger,
  PopoverContent
} from '@enfomentis/ui';
import { LocalDirectorySyncProvider } from '@/services/sync/LocalDirectorySyncProvider';
import type { SyncProviderType } from '@/types/sync';

export interface StorageLocationCardProps {
  /** Active sync provider type */
  provider: SyncProviderType | undefined;
  /** Callback when directory connected */
  onConnect: (forceNew?: boolean) => Promise<void>;
  /** Callback when directory disconnected */
  onDisconnect: () => Promise<void>;
}

/**
 * Storage Location Card
 *
 * Displays connection status and manages directory picker for local sync.
 * Only shown when provider is 'local'.
 */
export const StorageLocationCard: React.FC<StorageLocationCardProps> = ({
  provider,
  onConnect,
  onDisconnect
}) => {
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [directoryPath, setDirectoryPath] = useState<string>('');
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const [syncProvider] = useState(() => new LocalDirectorySyncProvider());

  // Check connection status on mount and when provider changes
  useEffect(() => {
    const checkConnection = async (): Promise<void> => {
      if (provider === 'local') {
        const connected = await syncProvider.isConnected();
        setIsConnected(connected);

        // Get directory name from provider
        if (connected) {
          const dirName = await syncProvider.getDirectoryName();
          setDirectoryPath(dirName || 'Connected to local directory');
        } else {
          setDirectoryPath('');
        }
      } else {
        setIsConnected(false);
        setDirectoryPath('');
      }
    };

    checkConnection();
  }, [provider, syncProvider]);

  const handleConnect = async (forceNew = false): Promise<void> => {
    setIsConnecting(true);
    try {
      await onConnect(forceNew);
      setIsConnected(true);

      // Get the directory name after connection
      // Create a fresh provider instance to ensure we read the latest handle from IndexedDB
      const freshProvider = new LocalDirectorySyncProvider();
      const dirName = await freshProvider.getDirectoryName();
      setDirectoryPath(dirName || 'Connected to local directory');
    } catch (error) {
      console.error('Failed to connect to directory:', error);
      setIsConnected(false);
      setDirectoryPath('');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async (): Promise<void> => {
    try {
      await onDisconnect();
      setIsConnected(false);
      setDirectoryPath('');
    } catch (error) {
      console.error('Failed to disconnect from directory:', error);
    }
  };

  // Show for local provider OR when no provider is set (so user can connect)
  // Hide only if a different provider is explicitly set (e.g., 'google-drive', 'dropbox')
  if (provider && provider !== 'local') {
    return null;
  }

  return (
    <Card>
      <CardHeader className="flex items-center gap-2">
        <HardDrive className="w-5 h-5 text-text-secondary" />
        <h3 className="text-lg font-semibold">Storage Location</h3>
        <Chip
          size="sm"
          color={isConnected ? 'success' : 'default'}
          variant="flat"
          className="ml-auto"
        >
          {isConnected ? 'Connected' : 'Not Connected'}
        </Chip>
      </CardHeader>
      <CardBody className="space-y-4 p-4 sm:p-6">
        {/* Directory Path Display */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <p className="text-sm text-text-secondary">Directory</p>
            {isConnected && (
              <Popover placement="top">
                <PopoverTrigger>
                  <button className="text-text-tertiary hover:text-text-secondary transition-colors">
                    <Info className="w-4 h-4" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="max-w-sm">
                  <div className="p-2">
                    <h4 className="font-semibold text-sm mb-2">Security & Privacy</h4>
                    <p className="text-xs text-text-secondary break-words">
                      For security reasons, browsers only provide the folder name, not the full path.
                      This protects your privacy by preventing websites from learning about your file system structure.
                    </p>
                    <p className="text-xs text-text-secondary mt-2 break-words">
                      You selected this folder using the directory picker, so you know which folder it is.
                    </p>
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
          {isConnected && directoryPath ? (
            <Code className="w-full p-2 text-sm break-all">
              {directoryPath}
            </Code>
          ) : (
            <p className="text-sm text-text-tertiary italic">
              No directory connected
            </p>
          )}
        </div>

        {/* Connection Actions */}
        <div className="flex flex-col sm:flex-row gap-2">
          {!isConnected ? (
            <Button
              color="primary"
              size="lg"
              startContent={<FolderOpen className="w-4 h-4" />}
              onPress={() => handleConnect()}
              isLoading={isConnecting}
              className="w-full sm:w-auto"
            >
              Connect to Local Directory
            </Button>
          ) : (
            <>
              <Button
                color="primary"
                variant="bordered"
                size="lg"
                startContent={<LinkIcon className="w-4 h-4" />}
                onPress={() => handleConnect(true)}
                isLoading={isConnecting}
                className="w-full sm:w-auto"
              >
                Change Directory
              </Button>
              <Button
                color="danger"
                variant="light"
                size="lg"
                startContent={<Unlink className="w-4 h-4" />}
                onPress={handleDisconnect}
                className="w-full sm:w-auto"
              >
                Disconnect
              </Button>
            </>
          )}
        </div>

        {/* Help Text */}
        <p className="text-xs text-text-tertiary break-words">
          Select a local folder where your sync files will be stored. Your browser will ask for permission
          to read and write files in this location.
        </p>
      </CardBody>
    </Card>
  );
};
